import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import LogsTab from './LogsTab';
import '../styles/v2/pages/entry-device-detail.css';
import {
  Tablet, ArrowLeft, RefreshCw, Signal, SignalZero, AlertTriangle,
  CheckCircle, XCircle, Clock, Cpu, HardDrive, Wifi, WifiOff,
  Eye, Send, Trash2, Settings, RotateCcw, Power, Database,
  FileText, ChevronDown, ChevronRight, Activity, User, Shield,
  Camera, MapPin, Tag, Box, Smartphone, Globe, Zap, Terminal,
  X, Package, Download
} from 'lucide-react';

const STATUS_CONFIG = {
  ONLINE:         { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)',  label: 'Online' },
  OFFLINE:        { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)',  label: 'Offline' },
  UPDATING:       { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', label: 'Updating' },
  ERROR:          { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)',  label: 'Error' },
  DECOMMISSIONED: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', label: 'Decommissioned' },
};

const EVENT_CONFIG = {
  ACCESS_GRANTED:        { color: '#22c55e', icon: CheckCircle, label: 'Granted' },
  ACCESS_DENIED:         { color: '#ef4444', icon: XCircle, label: 'Denied' },
  NOT_RECOGNIZED:        { color: '#f59e0b', icon: AlertTriangle, label: 'Not Recognized' },
  ANTI_SPOOF_TRIGGERED:  { color: '#ef4444', icon: Shield, label: 'Anti-Spoof' },
  TAILGATING_DETECTED:   { color: '#f59e0b', icon: AlertTriangle, label: 'Tailgating' },
  DOOR_HELD_OPEN:        { color: '#f59e0b', icon: Clock, label: 'Door Held' },
  DEVICE_ONLINE:         { color: '#22c55e', icon: Signal, label: 'Online' },
  DEVICE_OFFLINE:        { color: '#ef4444', icon: SignalZero, label: 'Offline' },
  CONFIG_APPLIED:        { color: '#3b82f6', icon: Settings, label: 'Config Applied' },
  UPDATE_INSTALLED:      { color: '#22c55e', icon: Zap, label: 'Updated' },
  UPDATE_FAILED:         { color: '#ef4444', icon: XCircle, label: 'Update Failed' },
  ERROR:                 { color: '#ef4444', icon: AlertTriangle, label: 'Error' },
};

const COMMAND_CONFIG = {
  RESTART_APP:    { icon: RotateCcw, label: 'Restart App', desc: 'Restart the Entry application' },
  REBOOT_DEVICE:  { icon: Power, label: 'Reboot Device', desc: 'Full device reboot' },
  CLEAR_CACHE:    { icon: Database, label: 'Clear Cache', desc: 'Clear app cache and temp files' },
  SYNC_CONFIG:    { icon: RefreshCw, label: 'Sync Config', desc: 'Force configuration re-sync' },
  PUSH_UPDATE:    { icon: Zap, label: 'Push Update', desc: 'Push an OTA update' },
  RETRIEVE_LOGS:  { icon: FileText, label: 'Retrieve Logs', desc: 'Pull device logs to CCM' },
  FORCE_CHECKIN:  { icon: Signal, label: 'Force Check-in', desc: 'Trigger immediate heartbeat' },
};

const CMD_STATUS_COLORS = {
  PENDING:      '#f59e0b',
  SENT:         '#3b82f6',
  ACKNOWLEDGED: '#a855f7',
  COMPLETED:    '#22c55e',
  FAILED:       '#ef4444',
  EXPIRED:      '#6b7280',
};

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return Math.floor(diff / 86400000) + 'd ago';
}

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

const EntryDeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingCommand, setSendingCommand] = useState(null);
  const [showDecommission, setShowDecommission] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [profiles, setProfiles] = useState([]);
  const [assigningProfile, setAssigningProfile] = useState(false);
  const [pushingConfig, setPushingConfig] = useState(false);
  const [clearingOverrides, setClearingOverrides] = useState(false);
  const [showPushApk, setShowPushApk] = useState(false);
  const [recentPushes, setRecentPushes] = useState([]);
  const [reenrolling, setReenrolling] = useState(false);

  const fetchRecentPushes = useCallback(async () => {
    if (!user?.isPlatformAdmin) return;
    try {
      const res = await fetch('/api/platform/entry/devices/' + id + '/pushes', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRecentPushes(data.pushes || []);
      }
    } catch (e) { /* swallow — non-critical */ }
  }, [id, user?.isPlatformAdmin]);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/entry/profiles');
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (e) { console.error('Failed to fetch profiles:', e); }
  }, []);

  const fetchDevice = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id);
      if (res.ok) {
        const data = await res.json();
        setDevice(data.device);
      } else {
        console.error('Failed to fetch device');
      }
    } catch (e) {
      console.error('Fetch device error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => { fetchDevice(); fetchProfiles(); fetchRecentPushes(); }, [fetchDevice, fetchProfiles, fetchRecentPushes]);

  // Auto-refresh every 15s
  useEffect(() => {
    const interval = setInterval(() => fetchDevice(), 15000);
    return () => clearInterval(interval);
  }, [fetchDevice]);

  // Faster poll while a push is in-flight (status not terminal). Drives the
  // status panel through Preflight → Downloading → Verified → Installed.
  useEffect(() => {
    const inFlight = recentPushes.some(p =>
      ['PENDING', 'DOWNLOADING', 'VERIFIED'].includes(p.status)
    );
    if (!inFlight) return;
    const t = setInterval(() => fetchRecentPushes(), 4000);
    return () => clearInterval(t);
  }, [recentPushes, fetchRecentPushes]);

  const handleRefresh = () => { setRefreshing(true); fetchDevice(); };

  const handleAssignProfile = async (profileId) => {
    setAssigningProfile(true);
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId: profileId || null }),
      });
      if (res.ok) {
        fetchDevice();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to assign profile');
      }
    } catch (e) { console.error(e); }
    finally { setAssigningProfile(false); }
  };

  const handlePushConfig = async () => {
    setPushingConfig(true);
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id + '/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commandType: 'SYNC_CONFIG' }),
      });
      if (res.ok) {
        fetchDevice();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to push config');
      }
    } catch (e) { console.error(e); }
    finally { setPushingConfig(false); }
  };

  const handleClearOverrides = async () => {
    setClearingOverrides(true);
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id + '/clear-overrides', {
        method: 'POST',
      });
      if (res.ok) {
        fetchDevice();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to clear overrides');
      }
    } catch (e) { console.error(e); }
    finally { setClearingOverrides(false); }
  };

  const handleSendCommand = async (commandType) => {
    setSendingCommand(commandType);
    const label = COMMAND_CONFIG[commandType]?.label || commandType.replace(/_/g, ' ');
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id + '/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commandType }),
      });
      if (res.ok) {
        toast.success(label + ' sent');
        fetchDevice();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to send ' + label);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to send ' + label);
    } finally {
      setSendingCommand(null);
    }
  };

  const handleDecommission = async () => {
    const deviceName = device?.name || 'Device';
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id + '/decommission', {
        method: 'POST',
      });
      if (res.ok) {
        toast.success(deviceName + ' decommissioned');
        navigate('/entry');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to decommission device');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to decommission device');
    }
  };

  const handleReenroll = async () => {
    const deviceName = device?.name || 'Device';
    setReenrolling(true);
    try {
      const res = await fetchWithTenant('/api/entry/devices/' + id + '/reenroll', {
        method: 'POST',
      });
      if (res.ok) {
        toast.success(deviceName + ' re-enrolled');
        setShowDecommission(false);
        fetchDevice();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.error || 'Failed to re-enroll device');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to re-enroll device');
    } finally {
      setReenrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="entry-detail">
        <div className="entry-detail-loading">
          <RefreshCw size={24} className="spin" />
          <span>Loading device...</span>
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="entry-detail">
        <div className="entry-detail-empty">
          <AlertTriangle size={32} />
          <h3>Device Not Found</h3>
          <button className="btn btn-secondary" onClick={() => navigate('/entry')}>
            <ArrowLeft size={16} /> Back to Fleet
          </button>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[device.status] || STATUS_CONFIG.OFFLINE;

  return (
    <div className="entry-detail">
      {/* Header */}
      <div className="entry-detail-header">
        <div className="entry-detail-header-left">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/entry')}>
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1>
              <Tablet size={22} /> {device.name}
            </h1>
            <div className="entry-detail-subtitle">
              {device.siteLabel && <span><MapPin size={12} /> {device.siteLabel}</span>}
              {device.zoneLabel && <span>{device.zoneLabel}</span>}
              <span className="entry-detail-id">{device.deviceId}</span>
            </div>
          </div>
        </div>
        <div className="entry-detail-header-right">
          <span className="entry-detail-status" style={{ background: sc.bg, color: sc.color }}>
            {sc.label}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="entry-detail-tabs">
        {['overview', 'events', 'commands', 'logs', 'settings'].map(tab => (
          <button
            key={tab}
            className={"entry-detail-tab" + (activeSection === tab ? ' active' : '')}
            onClick={() => setActiveSection(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeSection === 'overview' && (
        <div className="entry-detail-section">
          {/* Device Info Cards */}
          <div className="entry-detail-grid">
            {/* Hardware Card */}
            <div className="entry-info-card">
              <h3><Box size={16} /> Hardware</h3>
              <div className="entry-info-rows">
                <div className="entry-info-row">
                  <span className="entry-info-label">Manufacturer</span>
                  <span className="entry-info-value">{device.manufacturer || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Model</span>
                  <span className="entry-info-value">{device.model || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Serial</span>
                  <span className="entry-info-value">{device.serialNumber || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">OS</span>
                  <span className="entry-info-value">{device.osVersion || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">MAC Address</span>
                  <span className="entry-info-value mono">{device.macAddress || '\u2014'}</span>
                </div>
              </div>
            </div>

            {/* Network Card */}
            <div className="entry-info-card">
              <h3><Globe size={16} /> Network</h3>
              <div className="entry-info-rows">
                <div className="entry-info-row">
                  <span className="entry-info-label">IP Address</span>
                  <span className="entry-info-value mono">{device.ipAddress || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">CCM Latency</span>
                  <span className="entry-info-value" style={{ color: device.networkLatencyMs > 500 ? '#ef4444' : device.networkLatencyMs > 200 ? '#f59e0b' : '#22c55e' }}>
                    {device.networkLatencyMs != null ? device.networkLatencyMs + 'ms' : '\u2014'}
                  </span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">OnWatch Latency</span>
                  <span className="entry-info-value" style={{ color: device.oostoLatencyMs > 500 ? '#ef4444' : device.oostoLatencyMs > 200 ? '#f59e0b' : '#22c55e' }}>
                    {device.oostoLatencyMs != null ? device.oostoLatencyMs + 'ms' : '\u2014'}
                  </span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Last Heartbeat</span>
                  <span className="entry-info-value">{timeAgo(device.lastHeartbeat)}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Last Seen</span>
                  <span className="entry-info-value">{formatDate(device.lastSeen)}</span>
                </div>
              </div>
            </div>

            {/* Software Card */}
            <div className="entry-info-card">
              <h3><Smartphone size={16} /> Software</h3>
              <div className="entry-info-rows">
                <div className="entry-info-row">
                  <span className="entry-info-label">App Version</span>
                  <span className="entry-info-value">{device.appVersion || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Package</span>
                  <span className="entry-info-value mono">{device.appPackage || '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Config Profile</span>
                  <span className="entry-info-value">{device.profile?.name || 'None assigned'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Enrolled</span>
                  <span className="entry-info-value">{formatDate(device.enrolledAt)}</span>
                </div>
              </div>
            </div>

            {/* Health Card */}
            <div className="entry-info-card">
              <h3><Activity size={16} /> Health</h3>
              <div className="entry-info-rows">
                <div className="entry-info-row">
                  <span className="entry-info-label">Camera</span>
                  <span className="entry-info-value" style={{
                    color: device.cameraHealth === 'healthy' ? '#22c55e' : device.cameraHealth === 'degraded' ? '#f59e0b' : device.cameraHealth === 'down' ? '#ef4444' : '#64748b'
                  }}>
                    {device.cameraHealth ? device.cameraHealth.charAt(0).toUpperCase() + device.cameraHealth.slice(1) : '\u2014'}
                  </span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Disk Usage</span>
                  <span className="entry-info-value">
                    {device.diskUsagePercent != null ? (
                      <span className="entry-health-bar-wrap">
                        <span style={{ color: device.diskUsagePercent > 90 ? '#ef4444' : device.diskUsagePercent > 70 ? '#f59e0b' : '#22c55e' }}>
                          {Math.round(device.diskUsagePercent)}%
                        </span>
                        <span className="entry-health-bar-mini">
                          <span className="entry-health-bar-fill" style={{
                            width: Math.min(100, device.diskUsagePercent) + '%',
                            background: device.diskUsagePercent > 90 ? '#ef4444' : device.diskUsagePercent > 70 ? '#f59e0b' : '#22c55e'
                          }} />
                        </span>
                      </span>
                    ) : '\u2014'}
                  </span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">CPU</span>
                  <span className="entry-info-value">{device.cpuUsagePercent != null ? Math.round(device.cpuUsagePercent) + '%' : '\u2014'}</span>
                </div>
                <div className="entry-info-row">
                  <span className="entry-info-label">Memory</span>
                  <span className="entry-info-value">{device.memUsagePercent != null ? Math.round(device.memUsagePercent) + '%' : '\u2014'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Commands */}
          <div className="entry-commands-section">
            <h3><Terminal size={16} /> Quick Commands</h3>
            <div className="entry-commands-grid">
              {Object.entries(COMMAND_CONFIG).map(([type, cfg]) => {
                const Icon = cfg.icon;
                const isSending = sendingCommand === type;
                // PUSH_UPDATE now opens a modal that picks an APK and routes
                // to the Platform-Admin push endpoint. Hidden for non-Platform
                // Admins since selecting an APK is a platform-level action.
                if (type === 'PUSH_UPDATE') {
                  if (!user?.isPlatformAdmin) return null;
                  return (
                    <button
                      key={type}
                      className="entry-command-btn"
                      onClick={() => setShowPushApk(true)}
                      disabled={device.status === 'DECOMMISSIONED'}
                    >
                      <Icon size={16} />
                      <span className="entry-command-label">{cfg.label}</span>
                      <span className="entry-command-desc">{cfg.desc}</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={type}
                    className="entry-command-btn"
                    onClick={() => handleSendCommand(type)}
                    disabled={isSending || device.status === 'DECOMMISSIONED'}
                  >
                    <Icon size={16} className={isSending ? 'spin' : ''} />
                    <span className="entry-command-label">{cfg.label}</span>
                    <span className="entry-command-desc">{cfg.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* In-flight APK push status panel (Platform Admins only) */}
          {user?.isPlatformAdmin && recentPushes.length > 0 && (
            <ApkPushStatusPanel pushes={recentPushes} onRefresh={fetchRecentPushes} />
          )}
        </div>
      )}

      {/* Events Tab */}
      {activeSection === 'events' && (
        <div className="entry-detail-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)', marginBottom: '12px' }}>
            <Activity size={16} /> Recent Events
          </h3>
          {(!device.events || device.events.length === 0) ? (
            <div className="entry-detail-empty-section">
              <Activity size={32} style={{ opacity: 0.3 }} />
              <p>No events recorded yet. Events will appear here once the tablet starts processing access requests.</p>
            </div>
          ) : (
            <div className="entry-events-list">
              {device.events.map(event => {
                const ec = EVENT_CONFIG[event.eventType] || EVENT_CONFIG.ERROR;
                const EventIcon = ec.icon;
                return (
                  <div key={event.id} className="entry-event-row">
                    <div className="entry-event-icon" style={{ color: ec.color }}>
                      <EventIcon size={16} />
                    </div>
                    <div className="entry-event-info">
                      <span className="entry-event-type" style={{ color: ec.color }}>{ec.label}</span>
                      {event.personName && <span className="entry-event-person"><User size={12} /> {event.personName}</span>}
                      {event.reason && <span className="entry-event-reason">{event.reason}</span>}
                    </div>
                    <div className="entry-event-meta">
                      {event.confidence != null && (
                        <span className="entry-event-confidence">{Math.round(event.confidence * 100)}%</span>
                      )}
                      <span className="entry-event-time">{timeAgo(event.occurredAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Commands Tab */}
      {activeSection === 'commands' && (
        <div className="entry-detail-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)', marginBottom: '12px' }}>
            <Send size={16} /> Command History
          </h3>
          {(!device.commands || device.commands.length === 0) ? (
            <div className="entry-detail-empty-section">
              <Send size={32} style={{ opacity: 0.3 }} />
              <p>No commands sent yet. Use the Quick Commands on the Overview tab to send commands to this device.</p>
            </div>
          ) : (
            <div className="entry-commands-list">
              {device.commands.map(cmd => (
                <div key={cmd.id} className="entry-command-row">
                  <div className="entry-command-type">
                    <span className="entry-command-type-label">{cmd.commandType.replace(/_/g, ' ')}</span>
                    <span className="entry-command-status" style={{
                      color: CMD_STATUS_COLORS[cmd.status] || '#6b7280',
                      background: (CMD_STATUS_COLORS[cmd.status] || '#6b7280') + '20'
                    }}>
                      {cmd.status}
                    </span>
                  </div>
                  <div className="entry-command-times">
                    <span>Created: {formatDate(cmd.createdAt)}</span>
                    {cmd.sentAt && <span>Sent: {timeAgo(cmd.sentAt)}</span>}
                    {cmd.completedAt && <span>Completed: {timeAgo(cmd.completedAt)}</span>}
                    {cmd.error && <span className="entry-command-error">{cmd.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeSection === 'logs' && (
        <LogsTab deviceId={id} deviceName={device?.name} />
      )}

      {activeSection === 'settings' && (
        <div className="entry-detail-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)', marginBottom: '16px' }}>
            <Settings size={16} /> Device Settings
          </h3>

          {/* Config Profile Assignment */}
          <div className="entry-info-card" style={{ marginBottom: '16px' }}>
            <h3><Tag size={16} /> Configuration Profile</h3>
            <div className="entry-profile-assign">
              <div className="entry-profile-assign-row">
                <div className="entry-profile-assign-select-wrap">
                  <select
                    className="form-select"
                    value={device.profileId || ''}
                    onChange={e => handleAssignProfile(e.target.value || null)}
                    disabled={assigningProfile}
                    style={{ background: '#151a23', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', width: '100%' }}
                  >
                    <option value="">No profile assigned</option>
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (Default)' : ''}</option>
                    ))}
                  </select>
                  {assigningProfile && <RefreshCw size={14} className="spin" style={{ color: 'var(--text-tertiary)' }} />}
                </div>
                {device.profileId && (
                  <div className="entry-profile-assign-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handlePushConfig}
                      disabled={pushingConfig || device.status === 'DECOMMISSIONED'}
                      title="Push current profile config to this device"
                    >
                      <Send size={13} /> {pushingConfig ? 'Pushing...' : 'Push Config'}
                    </button>
                  </div>
                )}
              </div>
              {device.profile && (
                <div className="entry-profile-assign-info">
                  {device.profile.description && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>{device.profile.description}</span>
                  )}
                  <div className="entry-profile-sections-list">
                    {['oostoConfig', 'rtspConfig', 'uiConfig', 'kioskConfig', 'securityConfig', 'networkConfig'].map(key => {
                      const val = device.profile[key];
                      const hasConfig = val && Object.keys(val).length > 0;
                      const labels = {
                        oostoConfig: 'OnWatch',
                        rtspConfig: 'RTSP',
                        uiConfig: 'UI/Branding',
                        kioskConfig: 'Kiosk',
                        securityConfig: 'Security',
                        networkConfig: 'Network',
                      };
                      return (
                        <span key={key} className={'entry-profile-section-dot ' + (hasConfig ? 'active' : 'empty')}>
                          {hasConfig ? <CheckCircle size={10} /> : null}
                          {labels[key]}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Config Drift Detection */}
          {device.localConfigOverrides && Object.keys(device.localConfigOverrides).length > 0 && (
            <div className="entry-info-card entry-warning-card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h3 style={{ margin: 0 }}><AlertTriangle size={16} /> Configuration Drift Detected</h3>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleClearOverrides}
                  disabled={clearingOverrides}
                  title="Clear local overrides and re-apply the assigned profile"
                >
                  <RotateCcw size={13} /> {clearingOverrides ? 'Clearing...' : 'Reset to Profile'}
                </button>
              </div>
              <p style={{ color: '#f59e0b', fontSize: '13px', marginBottom: '10px' }}>
                This device has settings that were modified locally and differ from the assigned profile.
                Click "Reset to Profile" to clear overrides and re-apply the profile configuration.
              </p>
              <div className="entry-drift-table">
                {Object.entries(device.localConfigOverrides).map(([section, fields]) => (
                  <div key={section} className="entry-drift-section">
                    <div className="entry-drift-section-header">{section.replace('Config', '').replace(/([A-Z])/g, ' $1').trim()}</div>
                    {typeof fields === 'object' && fields !== null ? (
                      Object.entries(fields).map(([field, value]) => {
                        const profileValue = device.profile?.[section]?.[field];
                        return (
                          <div key={field} className="entry-drift-row">
                            <span className="entry-drift-field">{field}</span>
                            <span className="entry-drift-profile">
                              {profileValue !== undefined ? String(profileValue) : '(not set)'}
                            </span>
                            <span className="entry-drift-arrow">\u2192</span>
                            <span className="entry-drift-local">{String(value)}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="entry-drift-row">
                        <span className="entry-drift-field">(raw override)</span>
                        <span className="entry-drift-local">{JSON.stringify(fields)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Map Placement */}
          <div className="entry-info-card" style={{ marginBottom: '16px' }}>
            <h3><MapPin size={16} /> Map Placement</h3>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '4px 0' }}>
              Place this tablet on a site map or floor plan. Use the <a href="/map" style={{ color: '#00d4aa' }}>Site Map</a> page to position devices visually, or set coordinates here.
            </p>
            {device.siteLabel && (
              <div className="entry-info-row" style={{ marginTop: '6px' }}>
                <span className="entry-info-label">Site</span>
                <span className="entry-info-value">{device.siteLabel}</span>
              </div>
            )}
            {device.zoneLabel && (
              <div className="entry-info-row">
                <span className="entry-info-label">Zone</span>
                <span className="entry-info-value">{device.zoneLabel}</span>
              </div>
            )}
          </div>

          {/* No drift indicator when profile is assigned and clean */}
          {device.profileId && (!device.localConfigOverrides || Object.keys(device.localConfigOverrides).length === 0) && (
            <div className="entry-info-card" style={{ marginBottom: '16px', borderColor: 'rgba(34, 197, 94, 0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#22c55e', fontSize: '13px' }}>
                <CheckCircle size={16} />
                <span>Device configuration matches the assigned profile. No drift detected.</span>
              </div>
            </div>
          )}

          {/* Danger Zone */}
          <div className="entry-info-card entry-danger-card">
            <h3><AlertTriangle size={16} /> Danger Zone</h3>
            {device.status === 'DECOMMISSIONED' ? (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                  This device is decommissioned. Re-enrolling restores it to the active fleet; the tablet must re-enroll with a new token to reconnect.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={handleReenroll}
                  disabled={reenrolling}
                >
                  <RotateCcw size={14} /> {reenrolling ? 'Re-enrolling…' : 'Re-enroll Device'}
                </button>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                  Decommissioning will revoke this device's credentials, disconnect it from CCM, and release its license slot. The device will continue operating in standalone mode.
                </p>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowDecommission(true)}
                >
                  <Trash2 size={14} /> Decommission Device
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Push APK Modal */}
      {showPushApk && (
        <PushApkModal
          device={device}
          onClose={() => setShowPushApk(false)}
          onPushed={() => {
            setShowPushApk(false);
            fetchRecentPushes();
            fetchDevice();
          }}
        />
      )}

      {/* Decommission Modal */}
      {showDecommission && (
        <div className="platform-modal-overlay" onClick={() => setShowDecommission(false)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Decommission Device</h3>
              <button onClick={() => setShowDecommission(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will permanently disconnect <strong>{device.name}</strong> from CCM. The device's API credentials will be revoked and its license slot released. Event history is preserved and the device can be re-enrolled later.
              </p>
              <div className="platform-delete-item">{device.name} ({device.deviceId})</div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDecommission(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDecommission}>Decommission</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PUSH_UPDATE modal + in-flight status panel ──────────────────────────
// Both restricted to Platform Admins by the parent component.

const PUSH_PHASE_ORDER = ['PENDING', 'DOWNLOADING', 'VERIFIED', 'INSTALLED'];
const PUSH_PHASE_LABEL = {
  PENDING: 'Preflight',
  DOWNLOADING: 'Downloading',
  VERIFIED: 'Verified',
  INSTALLED: 'Installed',
  FAILED: 'Failed',
};
const PUSH_PHASE_COLORS = {
  PENDING:     { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  DOWNLOADING: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  VERIFIED:    { color: '#a855f7', bg: 'rgba(168, 85, 247, 0.15)' },
  INSTALLED:   { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  FAILED:      { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
};

function ApkPushStatusPanel({ pushes, onRefresh }) {
  const latest = pushes[0];
  if (!latest) return null;
  const s = PUSH_PHASE_COLORS[latest.status] || PUSH_PHASE_COLORS.PENDING;
  const idx = PUSH_PHASE_ORDER.indexOf(latest.status);
  const isTerminal = latest.status === 'INSTALLED' || latest.status === 'FAILED';

  return (
    <div className="entry-info-card" style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}><Package size={16} /> Latest APK Push</h3>
        <button className="btn btn-secondary btn-sm" onClick={onRefresh} title="Refresh">
          <RefreshCw size={13} />
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 500 }}>
            {latest.apk?.version || '—'} <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>vc{latest.apk?.versionCode}</span>
          </div>
          <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
            Initiated {new Date(latest.initiatedAt).toLocaleString()} by {latest.initiator?.name || latest.initiator?.email || '—'}
          </div>
        </div>
        <span style={{ padding: '4px 12px', borderRadius: '4px', color: s.color, background: s.bg, fontSize: '12px', fontWeight: 500 }}>
          {PUSH_PHASE_LABEL[latest.status]}
        </span>
      </div>

      {/* Phase progress bar */}
      {latest.status !== 'FAILED' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '14px' }}>
          {PUSH_PHASE_ORDER.map((phase, i) => {
            const reached = i <= idx;
            const isCurrent = i === idx && !isTerminal;
            return (
              <div key={phase} style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                <div style={{
                  flex: 1, height: '4px', borderRadius: '2px',
                  background: reached ? PUSH_PHASE_COLORS[phase].color : 'rgba(255,255,255,0.08)',
                  opacity: isCurrent ? 0.6 : 1,
                  transition: 'background 200ms',
                }} />
                <span style={{
                  fontSize: '10px', minWidth: '70px',
                  color: reached ? PUSH_PHASE_COLORS[phase].color : 'var(--text-tertiary)',
                  fontWeight: isCurrent ? 600 : 400,
                }}>
                  {PUSH_PHASE_LABEL[phase]}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {latest.status === 'FAILED' && latest.failureReason && (
        <div style={{ marginTop: '10px', padding: '8px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#ef4444', fontSize: '12px', fontFamily: 'ui-monospace, monospace' }}>
          {latest.failureReason}
        </div>
      )}
    </div>
  );
}

function PushApkModal({ device, onClose, onPushed }) {
  const [apks, setApks] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [allowDowngrade, setAllowDowngrade] = useState(false);
  const [tested, setTested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/platform/entry/apks', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setApks(data.apks || []);
        } else if (res.status === 403) {
          setApks([]);
          setError('Platform Admin access required.');
        } else {
          setApks([]);
          setError('Failed to load APKs');
        }
      } catch (e) {
        setApks([]);
        setError('Network error loading APKs');
      }
    })();
  }, []);

  const selected = apks?.find(a => a.id === selectedId) || null;
  const isDowngrade = selected && device.appVersionCode != null && selected.versionCode < device.appVersionCode;
  const isSameVersion = selected && device.appVersionCode != null && selected.versionCode === device.appVersionCode;
  const blocked = !selected || !tested || (isDowngrade && !allowDowngrade);

  const submit = async () => {
    if (blocked) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/platform/entry/apks/' + selected.id + '/push', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: device.id,
          allowDowngrade: allowDowngrade && isDowngrade,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || ('Push failed (HTTP ' + res.status + ')'));
        setSubmitting(false);
        return;
      }
      onPushed();
    } catch (e) {
      setError(e.message || 'Network error');
      setSubmitting(false);
    }
  };

  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="platform-modal-header">
          <h3><Zap size={18} /> Push Update to {device.name}</h3>
          <button onClick={onClose} disabled={submitting}><X size={18} /></button>
        </div>
        <div className="platform-modal-body" style={{ display: 'grid', gap: '14px' }}>
          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '10px 12px', color: '#ef4444', fontSize: '13px' }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              {error}
            </div>
          )}

          <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
            <div>Current Version: <span style={{ color: 'var(--text-primary)' }}>{device.appVersion || 'unknown'}{device.appVersionCode != null ? ' (vc' + device.appVersionCode + ')' : ''}</span></div>
            {selected && (
              <div style={{ marginTop: '4px' }}>
                New Version: <span style={{ color: '#22c55e' }}>{selected.version} (vc{selected.versionCode})</span>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>APK <span style={{ color: '#ef4444' }}>*</span></label>
            {apks === null ? (
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                <RefreshCw size={12} className="spin" style={{ verticalAlign: 'middle' }} /> Loading...
              </div>
            ) : apks.length === 0 ? (
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                No active APKs. Upload one in Settings → Entry → APK Updates.
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                disabled={submitting}
                style={modalInputStyle}
              >
                <option value="">— Select an APK —</option>
                {apks.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.version} (vc{a.versionCode}, {a.buildType}) — {new Date(a.uploadedAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}
          </div>

          {isSameVersion && (
            <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '6px', padding: '10px 12px', color: '#60a5fa', fontSize: '12px' }}>
              This device already reports versionCode {device.appVersionCode}. The push will still go out — the tablet will reinstall.
            </div>
          )}

          {isDowngrade && (
            <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '6px', padding: '10px 12px' }}>
              <div style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 500, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={14} /> Downgrade detected
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '8px' }}>
                Selected versionCode {selected?.versionCode} is lower than the device's current versionCode {device.appVersionCode}. Check the box below if intentional.
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#f59e0b', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={allowDowngrade}
                  onChange={e => setAllowDowngrade(e.target.checked)}
                  disabled={submitting}
                />
                Allow downgrade
              </label>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: '10px 12px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '6px' }}>
            <input
              type="checkbox"
              checked={tested}
              onChange={e => setTested(e.target.checked)}
              disabled={submitting}
              style={{ marginTop: '2px' }}
            />
            <span>
              <strong style={{ color: '#ef4444' }}>I have tested this APK on a non-production tablet.</strong>{' '}
              <span style={{ color: 'var(--text-tertiary)', display: 'block', fontSize: '12px', marginTop: '2px' }}>
                Required. A bad APK pushed to a production tablet may require physical access to recover.
              </span>
            </span>
          </label>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-danger"
            onClick={submit}
            disabled={blocked || submitting}
            style={{ background: blocked ? undefined : '#ef4444' }}
          >
            {submitting ? <><RefreshCw size={14} className="spin" /> Pushing...</> : <><Zap size={14} /> Push Update</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalInputStyle = {
  width: '100%',
  background: '#151a23',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '13px',
};

export default EntryDeviceDetail;
